import { Component, Input, ViewChild, computed, effect, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DxDataGridComponent } from 'devextreme-angular';
import { AppstateService } from 'src/app/services/appstate.service';
import { GridService } from 'src/app/services/controls/grid.service';
import { DataService } from 'src/app/services/data.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { IBudgetantrag } from 'src/app/types/IBudgetantrag';
import { IFreigabekosten } from 'src/app/types/IFreigabekosten';
import { IBudgetantragKosten } from 'src/app/types/IBudgetantragKosten';
import { IRisikenChancen } from 'src/app/types/IRisikenchancen';
import { IFreigabeViewModel } from 'src/app/types/viewmodels/freigabe.viewmodel';
import { IWirtschaftsplan } from 'src/app/types/IWirtschaftsplan';
import { CellPreparedEvent } from 'devextreme/ui/data_grid';

@Component({
  selector: 'app-budget-dashboard',
  // standalone: true,
  // imports: [],
  templateUrl: './budget-dashboard.component.html',
  styleUrl: './budget-dashboard.component.css',
})
export class BudgetDashboardComponent {


  @Input()
  set projektid(id: number) {
    if (id) {
      this.appStateService.projektId.set(id);
      this.appStateService.projektInfoId.set(id);
    }
  }
  @Input()
  set baid(id: number) {
    this.appStateService.budgetantragId.set(id);
  }

  @ViewChild("gridRisikenChancen", { static: false })
  public gridRisikenChancen!: DxDataGridComponent;
  @ViewChild("gridKosten", { static: false })
  public gridKosten!: DxDataGridComponent;
  @ViewChild("gridFreigabe", { static: false })
  public gridFreigabe!: DxDataGridComponent;

  public antrag = computed<IBudgetantrag | null>(() => this.dataService.budgetantragDetails());
  public isBauControlling = computed<boolean>(() => this.antrag()?.verwaltungsbereichID == 1);
  public wirtschaftsplaene = computed<IWirtschaftsplan[] | null>(() => this.dataService.wirtschaftsplaene());
  public antragLageplan = computed<File>(() => this.dataService.budgetantragDetailsLageplan());
  public antragLageplanExtension = computed(() => this.antrag()?.lageplanDateiname ? this.antrag()?.lageplanDateiname!.split('.').pop()?.toLowerCase() : '');
  public antragLageplanUrl = computed(() => this.antragLageplan() ? URL.createObjectURL(this.antragLageplan()!) : '');
  public dataKosten = computed<IBudgetantragKosten[] | null>(() => (this.dataService.budgetantragKosten() ?? []).filter(x => !x.isHiddenInView));
  public dataRisikenChancen = computed<IRisikenChancen[] | null>(() => this.dataService.budgetantragRisikenChancen());
  public dataFreigabe = computed<IFreigabeViewModel>(() => this.dataService.budgetantragFreigabe());

  public closeButtonOptions: any = {
    text: "Beenden",
    stylingMode: "text",
    onClick: (e: any) => {
      this.gridRisikenChancen.instance.cancelEditData();
    }
  }

  constructor(public gridService: GridService,
    private activeRoute: ActivatedRoute,
    public navigationService: NavigationService,
    private dataService: DataService,
    private appStateService: AppstateService) {


    effect(() => {
	  if (this.antrag()!.projektID > 0 ) {
	      this.appStateService.projektId.set(this.antrag()!.projektID );
          this.appStateService.projektInfoId.set(this.antrag()!.projektID);
	  }
	}, { allowSignalWrites: true });
	
	effect(() => {
      const bezugsJahr = this.antrag()?.bezugsJahr ?? 0;
      if (bezugsJahr !== 0 && !isNaN(bezugsJahr))
        this.gridService.Bezugsjahr.set(bezugsJahr);
    }, { allowSignalWrites: true });

    effect(() => {
      if (this.dataKosten() == null) {
        this.gridKosten.instance.beginCustomLoading("Kostenherleitung wird geladen...");
      } else {
        this.gridKosten.instance.endCustomLoading();
      }
    });
    effect(() => {
      if (this.dataRisikenChancen() == null) {
        this.gridRisikenChancen.instance.beginCustomLoading("Risken und Chancen werden geladen...");
      } else {
        this.gridRisikenChancen.instance.endCustomLoading();
      }
    });
    effect(() => {
      if (this.dataFreigabe().freigabekosten == null) {
        this.gridFreigabe.instance.beginCustomLoading("FreigabeKosten werden geladen...");
      } else {
        this.gridFreigabe.instance.endCustomLoading();
      }
    });
  }

  onBackButtonClick() {
    if (this.appStateService.navigationMain() === 'projekte') {
      this.navigationService.navigateToProjektDetails(this.appStateService.projektId());
    } else {
      this.navigationService.navigateToBudgetantraege();
    }
  }

  onBudgetantragDetailsButtonClick() {
    this.navigationService.navigateToBudgetantragDetails(this.activeRoute);
  }

  onKostenherleitungDetailsButtonClick() {
    this.navigationService.navigateToBudgetantragKostenherleitung(this.activeRoute);
  }

  onKostenherleitungButtonClick(e: any) {
    if (e.row.rowType === "data") {
      this.navigationService.navigateToBudgetantragKostenherleitung(this.activeRoute);
    }
  }

  onKostenherleitungRowDblClick(e: any) {
    if (e.rowType === "data") {
      this.navigationService.navigateToBudgetantragKostenherleitung(this.activeRoute);
    }
  }

  onRisikenDetailsButtonClick() {
    this.navigationService.navigateToBudgetantragRisikenchancen(this.activeRoute);
  }

  onFreigabeDetailsButtonClick() {
    this.navigationService.navigateToBudgetantragFreigabe(this.activeRoute);
  }

  onFreigabeButtonClick(e: any) {
    if (e.row.rowType === "data") {
      this.navigationService.navigateToBudgetantragFreigabe(this.activeRoute);
    }
  }

  onFreigabeRowDblClick(e: any) {
    if (e.rowType === "data") {
      this.navigationService.navigateToBudgetantragFreigabe(this.activeRoute);
    }
  }

  onLageplanPdfClick() {
	const url = this.antragLageplanUrl();
	if (!url) return;

	window.open(url, '_blank', 'noopener,noreferrer');
  }

  onCellPrepared($event: CellPreparedEvent) {
    if ($event.rowType === "data") {
      if ($event.column.dataField === "rest" && $event.data.rest != 0) {
        $event.cellElement.style.cssText = "color: red;";
        $event.cellElement.style.textAlign = "end";
      }
    }
  }

}
